/**
 * API utilities with network failure handling and retry logic
 *
 * This module connects to Firebase Cloud Functions:
 * - Dev (emulator): http://localhost:5001/stilllovefilm/europe-west1/api
 * - Prod: https://europe-west1-stilllovefilm.cloudfunctions.net/api
 */

// Firebase project configuration
const FIREBASE_PROJECT_ID = import.meta.env.VITE_FIREBASE_PROJECT_ID || 'stilllovefilm';
const FIREBASE_REGION = 'europe-west1';
const EMULATOR_PORT = 5001;

/**
 * Get the API base URL dynamically based on the current hostname.
 * This allows the app to work when accessed via LAN/VPN (not localhost).
 * In production, use relative URLs (Firebase Hosting rewrites /api/** to Cloud Function).
 * In development, use the Firebase Emulator URL.
 */
export function getApiBaseUrl(): string {
  // If explicitly configured with a non-localhost URL, use it
  const envUrl = import.meta.env.VITE_API_URL;
  if (envUrl && !envUrl.includes('localhost')) {
    return envUrl;
  }

  if (typeof window !== 'undefined') {
    const { hostname, protocol } = window.location;

    // In production (non-localhost hostnames without port 5000/5001), use relative URL
    // Firebase Hosting rewrites /api/** to the Cloud Function
    const port = window.location.port;
    const isEmulatorPort = port === '5000' || port === '5001' || port === '4000';
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';

    if (!isLocalhost && !isEmulatorPort) {
      // Production: use relative URL which Firebase Hosting will rewrite
      return '/api';
    }

    // Development via LAN/VPN: use the same hostname with emulator port
    if (!isLocalhost) {
      return `${protocol}//${hostname}:${EMULATOR_PORT}/${FIREBASE_PROJECT_ID}/${FIREBASE_REGION}/api`;
    }
  }

  // Default fallback to Firebase Emulator on localhost
  return envUrl || `http://localhost:${EMULATOR_PORT}/${FIREBASE_PROJECT_ID}/${FIREBASE_REGION}/api`;
}

const API_BASE_URL = getApiBaseUrl();

/**
 * API error with structured information
 */
export class ApiError extends Error {
  constructor(
    public statusCode: number,
    message: string,
    public code?: string,
    public requestId?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /**
   * Check if this error is a rate limit error
   */
  isRateLimitError(): boolean {
    return this.statusCode === 429 || this.code === 'RATE_LIMIT_EXCEEDED';
  }

  /**
   * Check if this error is a network error
   */
  isNetworkError(): boolean {
    return this.code === 'NETWORK_ERROR';
  }

  /**
   * Check if this error is retryable
   */
  isRetryable(): boolean {
    return (
      this.isRateLimitError() || this.isNetworkError() || this.statusCode >= 500
    );
  }

  /**
   * Get a user-friendly error message
   */
  getUserMessage(): string {
    // Rate limit errors
    if (this.isRateLimitError()) {
      return 'Too many requests. Please wait a moment and try again.';
    }

    // Network errors
    if (this.isNetworkError()) {
      return 'Unable to connect. Please check your internet connection and try again.';
    }

    // Server errors
    if (this.statusCode >= 500) {
      return 'Something went wrong on our end. Please try again later.';
    }

    // Use the original message for client errors
    return this.message;
  }
}

/**
 * Retry configuration
 */
interface RetryConfig {
  maxRetries: number;
  baseDelayMs: number;
  maxDelayMs: number;
}

const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  baseDelayMs: 1000,
  maxDelayMs: 10000,
};

/**
 * Sleep for a given number of milliseconds
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Check if an error is retryable
 */
function isRetryableError(error: unknown): boolean {
  if (error instanceof ApiError) {
    return error.isRetryable();
  }

  // Network errors (fetch failed)
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return true;
  }

  return false;
}

/**
 * Execute a function with retry logic
 */
async function withRetry<T>(
  fn: () => Promise<T>,
  config: RetryConfig = DEFAULT_RETRY_CONFIG
): Promise<T> {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= config.maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      // Don't retry if not retryable
      if (!isRetryableError(error)) {
        throw lastError;
      }

      // Don't retry if last attempt
      if (attempt === config.maxRetries) {
        throw lastError;
      }

      // Calculate delay with exponential backoff and jitter
      const delay = Math.min(
        config.baseDelayMs * Math.pow(2, attempt) + Math.random() * 500,
        config.maxDelayMs
      );

      console.log(
        `Request failed (attempt ${attempt + 1}/${config.maxRetries + 1}), ` +
          `retrying in ${Math.round(delay)}ms`
      );

      await sleep(delay);
    }
  }

  throw lastError || new Error('Request failed');
}

/**
 * API request options
 */
interface ApiRequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  retry?: boolean | RetryConfig;
}

/**
 * Make an API request with error handling and optional retry
 */
export async function apiRequest<T = unknown>(
  endpoint: string,
  options: ApiRequestOptions = {}
): Promise<T> {
  const { body, retry = false, ...fetchOptions } = options;

  const url = endpoint.startsWith('http')
    ? endpoint
    : `${API_BASE_URL}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;

  const requestFn = async (): Promise<T> => {
    let response: Response;

    try {
      response = await fetch(url, {
        ...fetchOptions,
        headers: {
          'Content-Type': 'application/json',
          ...fetchOptions.headers,
        },
        body: body ? JSON.stringify(body) : undefined,
      });
    } catch {
      // Network error (fetch failed)
      throw new ApiError(
        0,
        'Network request failed. Please check your connection.',
        'NETWORK_ERROR'
      );
    }

    // Parse response body
    let data: Record<string, unknown> = {};
    try {
      const text = await response.text();
      if (text) {
        data = JSON.parse(text);
      }
    } catch {
      // Response wasn't JSON
    }

    // Handle error responses
    if (!response.ok) {
      throw new ApiError(
        response.status,
        (data.error as string) ||
          `Request failed with status ${response.status}`,
        data.code as string | undefined,
        data.requestId as string | undefined
      );
    }

    return data as T;
  };

  // Execute with or without retry
  if (retry) {
    const retryConfig =
      typeof retry === 'object' ? retry : DEFAULT_RETRY_CONFIG;
    return withRetry(requestFn, retryConfig);
  }

  return requestFn();
}

/**
 * Authenticated API request helper
 */
export async function authenticatedRequest<T = unknown>(
  endpoint: string,
  token: string,
  options: ApiRequestOptions = {}
): Promise<T> {
  return apiRequest<T>(endpoint, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${token}`,
    },
  });
}

/**
 * Format an error for display
 */
export function formatError(error: unknown): string {
  if (error instanceof ApiError) {
    return error.getUserMessage();
  }

  if (error instanceof Error) {
    // Check for common network errors
    if (error.message.includes('fetch') || error.message.includes('network')) {
      return 'Unable to connect. Please check your internet connection.';
    }
    return error.message;
  }

  return 'An unexpected error occurred. Please try again.';
}
